import { useQuery } from "@tanstack/react-query";
import { buildListParams, type DataGridQuery, type PageRequest } from "@/hooks/use-data-grid";
import { engineError } from "@/lib/engine-error";
import type { ListEnvelope, ProjectListRow } from "@/lib/profitability-api";
import { queryKeys } from "@/lib/query";

export function useProjects(
  query: DataGridQuery,
  page: PageRequest,
  range?: string,
  contractType?: string,
  /** Client Partner names to narrow to, or the literal "Unassigned". */
  clientPartner?: string[],
  /** "project": one row per project over the range. "month": project-months. */
  view: "project" | "month" = "month",
) {
  // Joined for the query KEY only -- it just has to change when the selection
  // does. The request below sends one param per name.
  const partnerKey = (clientPartner ?? []).join("|");
  return useQuery({
    queryKey: [
      ...queryKeys.projects(query, page),
      range ?? "default",
      contractType ?? "all",
      partnerKey || "any",
      view,
    ],
    queryFn: async (): Promise<ListEnvelope<ProjectListRow>> => {
      const params = buildListParams(query, page);
      if (range) params.set("range", range);
      // Always sent: the engine's own default is "month", the page's is not.
      params.set("view", view);
      // "all" is the default and stays out of the request entirely.
      if (contractType && contractType !== "all") params.set("contract_type", contractType);
      // Server-side, exactly as the accounts grid does it and for the same
      // reason: this list pages at 50 of 1,746 project-months, so a filter
      // applied in the browser would offer only the partners visible on the
      // current page and narrow that page alone.
      //
      // One param PER NAME, never a comma-joined string: the endpoint reads
      // every `client_partner` param but also splits each on commas, so a name
      // containing one would silently become two filters matching nobody.
      for (const partner of clientPartner ?? []) params.append("client_partner", partner);
      const response = await fetch(`/api/projects?${params.toString()}`);
      if (!response.ok) {
        throw await engineError(response, "Could not load projects");
      }
      return response.json();
    },
    // Month-end close data. Re-fetching on every window focus would hammer the
    // engine for numbers that change once a month.
    staleTime: 5 * 60 * 1000,
  });
}
